// src/pages/SaleInvoices.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, Search, Receipt, ChevronLeft, ChevronRight,
  Filter, X, Building2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { saleInvoicesAPI, projectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './TimesheetDetail.css';

const PER_PAGE = 25;

const STATUTS = [
  { value: 'NEW',        label: 'New',        color: '#6366F1', bg: 'rgba(99,102,241,0.10)' },
  { value: 'EN_ATTENTE', label: 'En attente', color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' },
  { value: 'VALIDEE',    label: 'Validée',    color: '#3B82F6', bg: 'rgba(59,130,246,0.10)' },
  { value: 'PAYEE',      label: 'Payée',      color: '#16A34A', bg: 'rgba(34,197,94,0.10)' },
  { value: 'ANNULEE',    label: 'Annulée',    color: '#EF4444', bg: 'rgba(239,68,68,0.10)' },
];

const TYPES = [
  { value: 'FACTURE_VENTE', label: 'Facture de vente' },
  { value: 'AVOIR',         label: 'Avoir' },
  { value: 'PROFORMA',      label: 'Proforma' },
];

function fmtNum(n) {
  return Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('fr-FR') : '—';
}
function StatutBadge({ statut }) {
  const s = STATUTS.find(x => x.value === statut);
  if (!s) return <span style={{ color: 'var(--text-faint)', fontSize: '0.8125rem' }}>—</span>;
  return (
    <span className="badge" style={{ color: s.color, background: s.bg, border: `1px solid ${s.color}33` }}>
      {s.label}
    </span>
  );
}

export default function SaleInvoices() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const canEdit   = ['super_admin','admin','business_manager','ingenieur_affaires'].includes((user?.role||'').toLowerCase());

  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters,     setFilters]     = useState({ statut: '', type_facture: '' });

  // Modal création — on force la sélection d'un projet
  const [modal,      setModal]      = useState(false);
  const [projects,   setProjects]   = useState([]);
  const [projId,     setProjId]     = useState('');
  const [projDevise, setProjDevise] = useState('EUR');
  const [projSearch, setProjSearch] = useState('');
  const [saving,     setSaving]     = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (search)              params.q            = search;
      if (filters.statut)      params.statut       = filters.statut;
      if (filters.type_facture) params.type_facture = filters.type_facture;
      const r = await saleInvoicesAPI.list(params);
      setItems(r.data.items || []);
      setPage(1);
    } catch { toast.error('Erreur chargement factures'); }
    finally { setLoading(false); }
  }, [search, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openModal = async () => {
    setProjId(''); setProjDevise('EUR'); setProjSearch('');
    setModal(true);
    try {
      const r = await projectsAPI.list({ limit: 500 });
      setProjects(r.data || []);
    } catch { toast.error('Erreur chargement projets'); }
  };

  const handleCreate = async () => {
    if (!projId) { toast.error('Sélectionner un projet'); return; }
    setSaving(true);
    try {
      const r = await saleInvoicesAPI.create({ projet_id: projId, statut: 'NEW', type_facture: 'FACTURE_VENTE', currency: projDevise });
      setModal(false);
      navigate(`/sale-invoices/${r.data.id}`);
    } catch { toast.error('Erreur création facture'); }
    finally { setSaving(false); }
  };

  const hasFilters = Object.values(filters).some(Boolean);
  const totalPages = Math.ceil(items.length / PER_PAGE);
  const pageItems  = items.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const filteredProjects = projects.filter(p =>
    !projSearch || p.nom.toLowerCase().includes(projSearch.toLowerCase()) || (p.client||'').toLowerCase().includes(projSearch.toLowerCase())
  );

  return (
    <div>
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>Factures de ventes</h1>
          <p>Facturation client — liée aux projets, feuilles de temps et ressources</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => setShowFilters(s => !s)}>
            <Filter size={15}/> Filtres
            {hasFilters && (
              <span style={{ marginLeft:4, background:'#3B82F6', color:'white', borderRadius:'50%',
                width:16, height:16, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'0.6rem' }}>
                {Object.values(filters).filter(Boolean).length}
              </span>
            )}
          </button>
          <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={15}/></button>
          {canEdit && (
            <button className="btn btn-primary" onClick={openModal}>
              <Plus size={15}/> Nouvelle facture
            </button>
          )}
        </div>
      </div>

      {/* ── Recherche ─────────────────────────────────────────── */}
      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <div style={{ position:'relative', flex:1 }}>
          <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }}/>
          <input
            style={{ paddingLeft:34, height:38, border:'1px solid var(--border)', borderRadius:8, width:'100%', background:'var(--bg-card)', color:'var(--text-primary)', outline:'none', boxSizing:'border-box' }}
            placeholder="Réf., émetteur, destinataire, description…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {(search || hasFilters) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilters({ statut:'', type_facture:'' }); }}>
            <X size={14}/> Réinitialiser
          </button>
        )}
      </div>

      {/* ── Filtres ────────────────────────────────────────────── */}
      {showFilters && (
        <div className="card" style={{ marginBottom:16, padding:'14px 16px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10 }}>
            <div>
              <label style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-faint)', display:'block', marginBottom:4 }}>Statut</label>
              <select className="filter-select" style={{ width:'100%' }} value={filters.statut}
                onChange={e => setFilters(p => ({ ...p, statut: e.target.value }))}>
                <option value="">Tous</option>
                {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-faint)', display:'block', marginBottom:4 }}>Type</label>
              <select className="filter-select" style={{ width:'100%' }} value={filters.type_facture}
                onChange={e => setFilters(p => ({ ...p, type_facture: e.target.value }))}>
                <option value="">Tous les types</option>
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── Pagination info ────────────────────────────────────── */}
      {!loading && items.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <span style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>
            {Math.min((page-1)*PER_PAGE+1, items.length)}–{Math.min(page*PER_PAGE, items.length)} sur {items.length} facture{items.length !== 1 ? 's' : ''}
          </span>
          {totalPages > 1 && (
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}><ChevronLeft size={14}/></button>
              {Array.from({length:totalPages},(_,i)=>i+1).map(p => (
                <button key={p} onClick={() => setPage(p)} className="btn btn-ghost btn-sm"
                  style={{ fontWeight:p===page?700:400, color:p===page?'#3B82F6':undefined, background:p===page?'rgba(59,130,246,0.08)':undefined, minWidth:28 }}>{p}</button>
              ))}
              <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}><ChevronRight size={14}/></button>
            </div>
          )}
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────── */}
      {loading ? (
        <div className="loading-state"><div className="spinner"/></div>
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:'48px 0' }}>
          <Receipt size={36} style={{ opacity:0.3, marginBottom:12, display:'block', margin:'0 auto 12px' }}/>
          <div style={{ fontWeight:600, marginBottom:6 }}>Aucune facture</div>
          <div style={{ fontSize:'0.875rem', color:'var(--text-muted)', marginBottom:20 }}>
            Les factures sont liées aux projets
          </div>
          {canEdit && (
            <button className="btn btn-primary" onClick={openModal}><Plus size={15}/> Créer</button>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Type</th>
                  <th>Projet</th>
                  <th>Émetteur</th>
                  <th>Destinataire</th>
                  <th>Date</th>
                  <th>Échéance</th>
                  <th style={{ textAlign:'right' }}>Total HT</th>
                  <th style={{ textAlign:'right' }}>Total TTC</th>
                  <th style={{ textAlign:'right' }}>Impayé</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map(inv => (
                  <tr key={inv.id} onClick={() => navigate(`/sale-invoices/${inv.id}`)} style={{ cursor:'pointer' }}>
                    <td>
                      <div style={{ fontWeight:700, color:'var(--orange-500)', fontSize:'0.875rem' }}>{inv.ref}</div>
                    </td>
                    <td>
                      <span style={{ fontSize:'0.7rem', padding:'2px 8px', borderRadius:6, background:'rgba(139,143,168,0.12)', color:'var(--text-muted)', border:'1px solid var(--border)', fontWeight:600 }}>
                        {TYPES.find(t=>t.value===inv.type_facture)?.label || inv.type_facture}
                      </span>
                    </td>
                    <td>
                      {inv.projet_nom ? (
                        <div>
                          <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{inv.projet_nom}</div>
                        </div>
                      ) : <span style={{ color:'var(--text-faint)' }}>—</span>}
                    </td>
                    <td style={{ fontSize:'0.8125rem' }}>{inv.emetteur || <span style={{ color:'var(--text-faint)' }}>—</span>}</td>
                    <td style={{ fontSize:'0.8125rem' }}>{inv.destinataire || <span style={{ color:'var(--text-faint)' }}>—</span>}</td>
                    <td style={{ fontSize:'0.8125rem', whiteSpace:'nowrap' }}>{fmtDate(inv.date_facture)}</td>
                    <td style={{ fontSize:'0.8125rem', whiteSpace:'nowrap' }}>{fmtDate(inv.date_echeance)}</td>
                    <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:'0.8125rem' }}>{fmtNum(inv.total_ht)} {inv.currency||'EUR'}</td>
                    <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:'0.875rem', fontWeight:600 }}>{fmtNum(inv.total_ttc)} {inv.currency||'EUR'}</td>
                    <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:'0.8125rem', color: inv.montant_impaye > 0 ? '#EF4444' : 'var(--text-faint)' }}>
                      {fmtNum(inv.montant_impaye)} {inv.currency||'EUR'}
                    </td>
                    <td><StatutBadge statut={inv.statut}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modal : choisir un projet ─────────────────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" style={{ maxWidth:480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouvelle facture — choisir un projet</h3>
              <button className="modal-close" onClick={() => setModal(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize:'0.875rem', color:'var(--text-muted)', marginBottom:14 }}>
                Une facture doit obligatoirement être associée à un projet.
              </p>
              <div style={{ position:'relative', marginBottom:12 }}>
                <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'var(--text-faint)' }}/>
                <input className="form-control" style={{ paddingLeft:30 }} placeholder="Rechercher un projet…"
                  value={projSearch} onChange={e => setProjSearch(e.target.value)}/>
              </div>
              <div style={{ maxHeight:280, overflowY:'auto', display:'flex', flexDirection:'column', gap:4 }}>
                {filteredProjects.length === 0 ? (
                  <div style={{ textAlign:'center', padding:24, color:'var(--text-faint)', fontSize:'0.875rem' }}>Aucun projet</div>
                ) : filteredProjects.map(p => (
                  <button key={p.id} type="button"
                    onClick={() => { setProjId(p.id); setProjDevise(p.devise || 'EUR'); }}
                    style={{
                      display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
                      borderRadius:8, border:`2px solid ${projId === p.id ? 'var(--orange-500)' : 'var(--border)'}`,
                      background: projId === p.id ? 'rgba(255,65,0,0.06)' : 'var(--bg-card)',
                      cursor:'pointer', textAlign:'left', transition:'border-color 0.15s', width:'100%',
                    }}>
                    <Building2 size={14} style={{ color:'var(--text-faint)', flexShrink:0 }}/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:'0.875rem', color:'var(--text-primary)' }}>{p.nom}</div>
                      {p.client && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{p.client}</div>}
                    </div>
                    <span style={{ fontSize:'0.7rem', padding:'2px 7px', borderRadius:5, background:'rgba(139,143,168,0.12)',
                      color:'var(--text-muted)', border:'1px solid var(--border)', fontWeight:700, flexShrink:0 }}>
                      {p.devise || 'EUR'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving || !projId}>
                {saving ? 'Création…' : 'Créer la facture'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
